/**
 * Disposal Portal Back-Office API
 * All calls require callerEmail from UserContext.
 */

import { apiFetch } from '../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

// ---------------------------------------------------------------------------
// Scrap Projects
// ---------------------------------------------------------------------------

export async function createScrapProject(payload, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_scrap_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'CREATE', ...payload, caller_email: callerEmail }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Create failed (${res.status})`); }
  return res.json();
}

export async function updateScrapProject(payload, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_scrap_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'UPDATE', ...payload, caller_email: callerEmail }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Update failed (${res.status})`); }
  return res.json();
}

export async function closeScrapProject(projectId, siteId, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_scrap_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'CLOSE', project_id: projectId, site_id: siteId, caller_email: callerEmail }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Close failed (${res.status})`); }
  return res.json();
}

export async function addToolsToProject(projectId, siteId, toolIds, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_scrap_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'ADD_TOOLS', project_id: projectId, site_id: siteId, tool_ids: toolIds, caller_email: callerEmail }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Add tools failed (${res.status})`); }
  return res.json();
}

export async function writeOffTool(projectId, siteId, toolId, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_scrap_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'WRITE_OFF_TOOL', project_id: projectId, site_id: siteId, tool_id: toolId, caller_email: callerEmail }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Write off failed (${res.status})`); }
  return res.json();
}

export async function removeToolFromProject(projectId, toolId, callerEmail, reason = '') {
  const res = await apiFetch(`${API_BASE_URL}/manage_scrap_project`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'REMOVE_TOOL', project_id: projectId, tool_id: toolId, caller_email: callerEmail, reason }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Remove failed (${res.status})`); }
  return res.json();
}

// ---------------------------------------------------------------------------
// Read endpoints (get_disposal_project_tools)
// ---------------------------------------------------------------------------

export async function getEligiblePool(siteId, callerEmail) {
  const params = new URLSearchParams({ mode: 'ELIGIBLE_POOL', site_id: siteId, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Fetch failed (${res.status})`); }
  return res.json();
}

export async function getProjectList(siteId, callerEmail) {
  const params = new URLSearchParams({ mode: 'PROJECT_LIST', site_id: siteId, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Fetch failed (${res.status})`); }
  return res.json();
}

export async function getProjectDetail(projectId, siteId, callerEmail) {
  const params = new URLSearchParams({ mode: 'PROJECT_DETAIL', project_id: projectId, site_id: siteId, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Fetch failed (${res.status})`); }
  return res.json();
}

export async function getProjectTools(projectId, callerEmail) {
  const params = new URLSearchParams({ mode: 'PROJECT_TOOLS', project_id: projectId, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Fetch failed (${res.status})`); }
  return res.json();
}

export async function getBolList(projectId, callerEmail) {
  const params = new URLSearchParams({ mode: 'BOL_LIST', project_id: projectId, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Fetch failed (${res.status})`); }
  return res.json();
}

export async function getBolDetail(bolId, projectId, callerEmail) {
  const params = new URLSearchParams({ mode: 'BOL_DETAIL', bol_id: bolId, project_id: projectId, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_disposal_project_tools?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Fetch failed (${res.status})`); }
  return res.json();
}

// ---------------------------------------------------------------------------
// BOL Management
// ---------------------------------------------------------------------------

export async function updateBolWeightAndInvoice(bolId, projectId, weightLbs, invoiced, callerEmail, opts = {}) {
  const res = await apiFetch(`${API_BASE_URL}/manage_bol`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'UPDATE_WEIGHT_AND_INVOICE', bol_id: bolId, project_id: projectId,
      weight_ticket_actual_lbs: weightLbs, invoiced, caller_email: callerEmail,
      ...opts,
    }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Update failed (${res.status})`); }
  return res.json();
}

export async function addBolDocument(bolId, projectId, document, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_bol`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'ADD_DOCUMENT', bol_id: bolId, project_id: projectId,
      document, caller_email: callerEmail,
    }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Add document failed (${res.status})`); }
  return res.json();
}

export async function deleteBolDocument(bolId, projectId, blobName, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_bol`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'DELETE_DOCUMENT', bol_id: bolId, project_id: projectId,
      blob_name: blobName, caller_email: callerEmail,
    }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Delete document failed (${res.status})`); }
  return res.json();
}

export async function createManualBol(payload, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_bol`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'CREATE', ...payload, caller_email: callerEmail }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Create BOL failed (${res.status})`); }
  return res.json();
}

export async function reopenBol(bolId, projectId, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/manage_bol`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'REOPEN', bol_id: bolId, project_id: projectId, caller_email: callerEmail,
    }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Reopen failed (${res.status})`); }
  return res.json();
}

// ---------------------------------------------------------------------------
// BOL Document Upload (SAS URL)
// ---------------------------------------------------------------------------

export async function getBolDocumentUploadUrl(bolId, originalFilename, fileSizeBytes, callerEmail) {
  const res = await apiFetch(`${API_BASE_URL}/get_bol_document_upload_url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      bol_id: bolId, original_filename: originalFilename,
      file_size_bytes: fileSizeBytes, caller_email: callerEmail,
    }),
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Upload URL failed (${res.status})`); }
  return res.json(); // { upload_url, blob_name }
}

export async function getBolDocumentDownloadUrl(blobName, callerEmail) {
  const params = new URLSearchParams({ blob_name: blobName, caller_email: callerEmail });
  const res = await apiFetch(`${API_BASE_URL}/get_bol_document_url?${params}`);
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Download URL failed (${res.status})`); }
  return res.json(); // { download_url }
}
